import React, { FC, useCallback, useState } from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { Option, OptionDataValue } from '../../../shared/options';
import { Address } from '../../models';
import { useTranslation } from '../../translations';
import { CountrySelector } from '../CountrySelector';
import { RegionSelector } from '../RegionSelector';

interface OptionTextProps {
  readonly option: Option;
  readonly disabled?: boolean;
  readonly defaultValue?: OptionDataValue;
  readonly onChange?: (id: number, option: OptionDataValue) => void;
}

const noCommas = (value: string) => value.replaceAll(',', '');
const addressToString = (address: Address) => {
  return `${noCommas(address.addressLine1)},${
    address.addressLine2 === undefined ? '' : noCommas(address.addressLine2)
  },${noCommas(address.addressCity)},${address.addressState},${address.addressCountry},${noCommas(
    address.addressZipcode,
  )}`;
};

const stringToAddress = (value: string) => {
  const params = value.split(',') as [string, string, string, string, string, string];
  const newAddress: Address = {
    addressLine1: params[0],
    addressLine2: params[1] === '' ? undefined : params[1],
    addressCity: params[2],
    addressState: params[3],
    addressCountry: params[4],
    addressZipcode: params[5],
  };

  return newAddress;
};

export const OptionAddressComponent: FC<OptionTextProps> = ({
  disabled,
  option,
  defaultValue,
  onChange,
}) => {
  const { ts } = useTranslation();
  const [address, setAddress] = useState<Address | null>(
    defaultValue === undefined ? null : stringToAddress(defaultValue as string),
  );

  const onAddressChange = useCallback(
    (key: keyof Address, value: string) => {
      let newAddress: Address;
      if (address === null) {
        newAddress = {
          addressCity: '',
          addressCountry: '',
          addressLine1: '',
          addressState: '',
          addressZipcode: '',
        };
      } else {
        newAddress = { ...address };
      }

      if (key === 'addressLine2' && value === '') {
        newAddress.addressLine2 = undefined;
      } else {
        newAddress[key] = value;
      }

      setAddress(newAddress);
      onChange?.(option.id, addressToString(newAddress));
    },
    [address, onChange, option.id],
  );

  return (
    <Row>
      <Col sm={6}>
        <FormGroup>
          <Label for="addressLine1">{ts('common.address_line_1')}</Label>
          <Input
            autoComplete="address-line1"
            disabled={disabled}
            id="addressLine1"
            name="addressLine1"
            onChange={(e) => {
              onAddressChange('addressLine1', e.currentTarget.value);
            }}
          />
        </FormGroup>
      </Col>
      <Col sm={6}>
        <FormGroup>
          <Label for="addressLine2">{ts('common.address_line_2_optional')}</Label>
          <Input
            autoComplete="address-line2"
            disabled={disabled}
            id="addressLine2"
            name="addressLine2"
            onChange={(e) => {
              onAddressChange('addressLine2', e.currentTarget.value);
            }}
          />
        </FormGroup>
      </Col>
      <Col sm={6}>
        <FormGroup>
          <Label for="addressCity">{ts('common.city')}</Label>
          <Input
            autoComplete="address-level2"
            disabled={disabled}
            id="addressCity"
            name="addressCity"
            onChange={(e) => {
              onAddressChange('addressCity', e.currentTarget.value);
            }}
          />
        </FormGroup>
      </Col>
      <Col sm={6}>
        <FormGroup>
          <Label for="addressZipcode">{ts('common.zip_postal_code')}</Label>
          <Input
            autoComplete="postal-code"
            disabled={disabled}
            id="addressZipcode"
            name="addressZipcode"
            onChange={(e) => {
              onAddressChange('addressZipcode', e.currentTarget.value);
            }}
          />
        </FormGroup>
      </Col>
      <Col sm={6}>
        <FormGroup>
          <Label for="addressCountry">{ts('common.country')}</Label>
          <CountrySelector
            autoComplete="country-name"
            className="form-control"
            disabled={disabled}
            id="addressCountry"
            name="addressCountry"
            onChange={(value) => {
              onAddressChange('addressCountry', value);
            }}
            value={address?.addressCountry ?? ''}
            valueType="short"
          />
        </FormGroup>
      </Col>
      <Col sm={6}>
        <FormGroup>
          <Label for="addressState">{ts('common.state_province')}</Label>
          <RegionSelector
            className="form-control"
            country={address?.addressCountry ?? ''}
            countryValueType="short"
            data-coerce={false}
            disableWhenEmpty
            disabled={disabled}
            id="addressState"
            name="addressState"
            onChange={(value) => {
              onAddressChange('addressState', value);
            }}
            value={address?.addressCountry ?? ''}
            valueType="short"
          />
        </FormGroup>
      </Col>
    </Row>
  );
};
